import React, {useState} from 'react';
import {styled} from '@mui/material/styles';
import PropTypes from 'prop-types';
import Switch from '@mui/material/Switch';
import SettingsPanel from '../components/SettingsPanel';

const PREFIX = 'FeaturesSettingsPanel';

const classes = {
  container: `${PREFIX}-container`,
  switchRow: `${PREFIX}-switchRow`,
};

const StyledSettingsPanel = styled(SettingsPanel)({
  [`& .${classes.container}`]: {
    width: '100%',
    padding: '1rem',
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  [`& .${classes.switchRow}`]: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    marginBottom: '1rem',
  },
});

const FeaturesSettingsPanel = ({ client, onSave }) => {
  const [expanded, setExpanded] = useState(false);
  const [useClientFuel, setUseClientFuel] = useState(client.use_client_fuel);
  const [reportCompanion, setReportCompanion] = useState(client.report_companion);
  const [features, setFeatures] = useState(client.features);
  const v1ReportsEnabled = features.includes('ftc_reports_v1');
  const v2ReportsEnabled = features.includes('ftc_reports_v2');
  const v3ReportsEnabled = features.includes('ftc_reports_v3');
  const analyticsEnabled = features.includes('analytics_module');
  const auditClusterReportsEnabled = features.includes('audit_cluster_reports');
  const auditTripReportsEnabled = features.includes('audit_trip_reports');
  const auditPointsFilesEnabled = features.includes('audit_points_files');
  const auditTablesEnabled = features.includes('audit_tables_files');
  const classRulingReportsEnabled = features.includes('class_ruling');
  const classRulingV3Enabled = features.includes('class_ruling_v3');
  const nuonicTelematicsIntegrationEnabled = features.includes('nuonic_telematics_integration');

  // Mirrors REPORT_TYPE_FAMILIES in api/clients/validation.py — only one flag
  // per family may be active at a time, but the two families are independent.
  const REPORT_TYPE_FAMILIES = {
    ftc_reports_v1: 'ftc',
    ftc_reports_v2: 'ftc',
    ftc_reports_v3: 'ftc',
    class_ruling: 'class_ruling',
    class_ruling_v3: 'class_ruling',
  };

  const toggleBooleanFeature = (checked, featureKey) => {
    if (checked === true) {
      setFeatures([...features, featureKey]);
    } else {
      const filteredFeatures = features.filter((f) => f !== featureKey);
      setFeatures(filteredFeatures);
    }
  };

  const toggleReportTypeFeature = (checked, featureKey) => {
    if (checked === true) {
      // Clear other flags in the same family — only one version per family may be active
      const family = REPORT_TYPE_FAMILIES[featureKey];
      const withoutSiblingFlags = features.filter((f) => REPORT_TYPE_FAMILIES[f] !== family);
      setFeatures([...withoutSiblingFlags, featureKey]);
    } else {
      setFeatures(features.filter((f) => f !== featureKey));
    }
  };

  const add_enabled_disabled = (str, enabled) => (enabled ? `${str} enabled` : `${str} disabled`);

  const summary = () => {
    const reportV1Summary = add_enabled_disabled('V1 FTC reports', v1ReportsEnabled);
    const reportV2Summary = add_enabled_disabled('V2 FTC reports', v2ReportsEnabled);
    const reportV3Summary = add_enabled_disabled('V3 FTC reports', v3ReportsEnabled);
    const fuelSummary = useClientFuel ? 'Actual fuel' : 'Estimated fuel';
    const reportCompanionSummary = add_enabled_disabled('Report Companion QR', reportCompanion);
    const analyticsSummary = add_enabled_disabled('Analytics', analyticsEnabled);
    const auditClusterReportSummary = add_enabled_disabled('Audit Cluster Reports', auditClusterReportsEnabled);
    const auditTripReportSummary = add_enabled_disabled('Audit Trip Reports', auditTripReportsEnabled);
    const auditPointsFilesSummary = add_enabled_disabled('Audit Points Files', auditPointsFilesEnabled);
    const auditTablesSummary = add_enabled_disabled('Audit Tables', auditTablesEnabled);
    const classRulingSummary = add_enabled_disabled('Class Ruling Reports', classRulingReportsEnabled);
    const classRulingV3Summary = add_enabled_disabled('Class Ruling v3 Reports', classRulingV3Enabled);
    const nuonicTelematicsIntegrationSummary = add_enabled_disabled('Nuonic Telematics Integration', nuonicTelematicsIntegrationEnabled);
    return [
      reportV1Summary,
      reportV2Summary,
      reportV3Summary,
      classRulingSummary,
      classRulingV3Summary,
      fuelSummary,
      reportCompanionSummary,
      analyticsSummary,
      auditClusterReportSummary,
      auditTripReportSummary,
      auditPointsFilesSummary,
      auditTablesSummary,
      nuonicTelematicsIntegrationSummary,
    ].join(', ');
  };

  const FeatureFlag = ({ checked, onChange, label }) => (
    <div className={classes.switchRow}>
      <Switch checked={checked} color="primary" onChange={onChange} />
      {label}
    </div>
  );
  FeatureFlag.propTypes = {
    checked: PropTypes.bool,
    onChange: PropTypes.func,
    label: PropTypes.string,
  };

  return (
    <StyledSettingsPanel
      title="Client Features"
      summary={summary()}
      onSave={() => {
        onSave({
          use_client_fuel: useClientFuel,
          report_companion: reportCompanion,
          features: features,
        });
        setExpanded(false);
      }}
      onCancel={() => {
        setUseClientFuel(client.use_client_fuel);
        setReportCompanion(client.report_companion);
        setFeatures(client.features);
        setExpanded(false);
      }}
      expanded={expanded}
      onExpand={() => setExpanded(!expanded)}
    >
      <div className={classes.container}>
        <FeatureFlag
          checked={v1ReportsEnabled}
          onChange={(e) => toggleReportTypeFeature(e.target.checked, 'ftc_reports_v1')}
          label="Version 1 FTC reports?"
        />
        <FeatureFlag
          checked={v2ReportsEnabled}
          onChange={(e) => toggleReportTypeFeature(e.target.checked, 'ftc_reports_v2')}
          label="Version 2 FTC reports?"
        />
        <FeatureFlag
          checked={v3ReportsEnabled}
          onChange={(e) => toggleReportTypeFeature(e.target.checked, 'ftc_reports_v3')}
          label="Version 3 FTC reports?"
        />
        <FeatureFlag
          checked={classRulingReportsEnabled}
          onChange={(e) => toggleReportTypeFeature(e.target.checked, 'class_ruling')}
          label="Enable Apportionment Reports?"
        />
        <FeatureFlag
          checked={classRulingV3Enabled}
          onChange={(e) => toggleReportTypeFeature(e.target.checked, 'class_ruling_v3')}
          label="Enable v3 Apportionment Reports?"
        />
        <FeatureFlag
          checked={useClientFuel}
          onChange={(e) => setUseClientFuel(e.target.checked)}
          label="Use Client-Entered Fuel in FTC Report?"
        />
        <FeatureFlag
          checked={reportCompanion}
          onChange={(e) => setReportCompanion(e.target.checked)}
          label="Show report companion QR on v3 reports?"
        />
        <FeatureFlag
          checked={analyticsEnabled}
          onChange={(e) => toggleBooleanFeature(e.target.checked, 'analytics_module')}
          label="Enable Analytics module?"
        />
        <FeatureFlag
          checked={auditClusterReportsEnabled}
          onChange={(e) => toggleBooleanFeature(e.target.checked, 'audit_cluster_reports')}
          label="Enable Audit Cluster Maps?"
        />
        <FeatureFlag
          checked={auditTripReportsEnabled}
          onChange={(e) => toggleBooleanFeature(e.target.checked, 'audit_trip_reports')}
          label="Enable Audit Trip Maps?"
        />
        <FeatureFlag
          checked={auditPointsFilesEnabled}
          onChange={(e) => toggleBooleanFeature(e.target.checked, 'audit_points_files')}
          label="Enable Audit Points Files?"
        />
        <FeatureFlag
          checked={auditTablesEnabled}
          onChange={(e) => toggleBooleanFeature(e.target.checked, 'audit_tables_files')}
          label="Enable Audit Tables Files?"
        />
        <FeatureFlag
          checked={nuonicTelematicsIntegrationEnabled}
          onChange={(e) => toggleBooleanFeature(e.target.checked, 'nuonic_telematics_integration')}
          label="Enable Nuonic Telematics Integration?"
        />
      </div>
    </StyledSettingsPanel>
  );
};

FeaturesSettingsPanel.propTypes = {
  client: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default FeaturesSettingsPanel;
